import React, {useContext} from 'react';
import {useTranslation} from 'react-i18next';

import {InlineNotification, InlineNotificationInterface} from '@veneer/core';

import {GlobalTranslationsContext} from '@br/idm-components';
import {PageTitle} from '@br/mg-components';

import {FormErrors, Message, OIDCLoginConfiguration, Party} from '../../types';
import {BasePage} from '../templates/BasePage';

import LoginOptions from './LoginOptions';

/**
 * Mapping of django message to veneer message categories
 */
const messageMapping: Record<string, InlineNotificationInterface['type']> = {
    debug: 'informative',
    info: 'informative',
    success: 'positive',
    warning: 'warning',
    error: 'negative',
};

const LogIn = ({
    loginConfiguration,
    errors,
    messages,
    initialUsername = '',
}: {
    loginConfiguration: OIDCLoginConfiguration;
    errors: FormErrors;
    messages: Message[];
    initialUsername?: string;
}): JSX.Element => {
    const {t} = useTranslation(['login']);
    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);
    const title: string =
        loginConfiguration.for_party === Party.Partner
            ? t('login:partnerLoginTitle', 'Partner login')
            : t('login:signInMessage', 'Sign in');

    return (
        <BasePage>
            <div className="d-flex align-items-center flex-column p-8">
                {messages.map(({message, level}) => (
                    <InlineNotification
                        role="alert"
                        key={message}
                        hideIcon={false}
                        title={message}
                        closeButton={false}
                        type={messageMapping[level]}
                        i18n={veneerTranslations.inlineNotification}
                    />
                ))}
                <PageTitle
                    logo
                    centered
                    htmlTitle={t('login:loginTitle', 'Log In')}
                    title={title}
                />
                <LoginOptions
                    loginConfiguration={loginConfiguration}
                    errors={errors}
                    initialUsername={initialUsername}
                />
            </div>
        </BasePage>
    );
};

export default LogIn;
